import { useProviderProfile } from '@/hooks/use-provider';
import { PackVocabulary } from '@/types/activity-pack';
import { useMemo } from 'react';

/**
 * Os termos que o app usa quando o prestador não tem pack aplicado — o app
 * de hoje, palavra por palavra.
 */
export const GENERIC_TERMS: PackVocabulary = {
  client: 'cliente',
  clientPlural: 'clientes',
  service: 'serviço',
  servicePlural: 'serviços',
  enrollment: 'contrato',
  enrollmentPlural: 'contratos',
  appointment: 'agendamento',
  appointmentPlural: 'agendamentos',
  organization: 'negócio',
  professional: 'profissional',
  professionalPlural: 'profissionais',
};

const TERM_KEYS = Object.keys(GENERIC_TERMS) as TermKey[];

export type TermKey = keyof PackVocabulary;
export type TermGender = 'm' | 'f';

/**
 * O gênero do substantivo, adivinhado pela terminação.
 *
 * Não é linguística: é o que impede "Nenhum aula encontrado" na tela. O
 * vocabulário vem de pack curado ou de IA, sempre em português e sempre um
 * substantivo comum — nesse recorte a terminação acerta o lote inteiro
 * (aula, matrícula e sessão femininos; aluno, tutor, pacote e horário
 * masculinos). Palavra que fugir da regra erra só o artigo.
 */
export function guessGender(term: string): TermGender {
  const word = term.trim().toLowerCase().split(/\s+/)[0] ?? '';
  // Plural primeiro: "aulas" e "sessões" precisam cair no mesmo lugar que o
  // singular delas.
  if (/(ções|sões|ões)$/.test(word)) return /(ções|sões)$/.test(word) ? 'f' : 'm';
  if (/(ção|são)$/.test(word)) return 'f';
  if (/as?$/.test(word)) return 'f';
  return 'm';
}

/**
 * As formas femininas dos determinantes e adjetivos que as telas usam ao lado
 * de um termo. Tabela fechada de propósito: transformar palavra qualquer por
 * regra ("-o" vira "-a") quebraria em "cliente" e "paciente".
 */
const FEMININE_FORMS: Record<string, string> = {
  o: 'a',
  um: 'uma',
  nenhum: 'nenhuma',
  novo: 'nova',
  este: 'esta',
  esse: 'essa',
  seu: 'sua',
  meu: 'minha',
  primeiro: 'primeira',
  proximo: 'proxima',
  próximo: 'próxima',
  outro: 'outra',
  todo: 'toda',
  do: 'da',
  no: 'na',
  pelo: 'pela',
  ao: 'à',
  selecionado: 'selecionada',
  marcado: 'marcada',
  agendado: 'agendada',
  pago: 'paga',
  ativo: 'ativa',
  inativo: 'inativa',
  criado: 'criada',
  encontrado: 'encontrada',
  cadastrado: 'cadastrada',
  vinculado: 'vinculada',
  concluido: 'concluida',
  concluído: 'concluída',
  cancelado: 'cancelada',
};

const matchCase = (source: string, target: string): string =>
  source[0] === source[0]?.toUpperCase()
    ? target.charAt(0).toUpperCase() + target.slice(1)
    : target;

/**
 * Concorda uma palavra escrita no masculino com o gênero de um termo.
 *
 * `agree('Nenhum', 'f')` devolve `'Nenhuma'`; o plural sai de graça, porque a
 * função tira o "s", concorda e devolve. Palavra fora da tabela volta como
 * veio — o pior caso é a tela continuar como está hoje.
 */
export function agree(masculine: string, gender: TermGender): string {
  if (gender === 'm') return masculine;

  const plural = /s$/.test(masculine);
  const singular = plural ? masculine.slice(0, -1) : masculine;
  const feminine = FEMININE_FORMS[singular.toLowerCase()];
  if (!feminine) return masculine;

  return matchCase(singular, feminine) + (plural ? 's' : '');
}

/** "alunos" -> "Alunos". Só a primeira letra: "Banho e tosa", não "Banho E Tosa". */
export function capitalizeTerm(term: string): string {
  if (!term) return term;
  return term.charAt(0).toUpperCase() + term.slice(1);
}

export interface Terms extends PackVocabulary {
  /** As mesmas chaves com inicial maiúscula, para título e cabeçalho. */
  title: PackVocabulary;
  /** O gênero de cada termo, para quem precisar decidir fora do `agree`. */
  gender: Record<TermKey, TermGender>;
  /**
   * `t.agree('Nenhum', 'service')` — escreva o texto no masculino e deixe a
   * concordância com o hook.
   */
  agree: (masculine: string, key: TermKey) => string;
  /** Há pack aplicado? Serve a texto de ajuda, não a esconder tela. */
  hasPack: boolean;
}

/**
 * Resolve o vocabulário guardado no prestador contra os termos genéricos.
 *
 * Fora do hook para o teste não precisar de react-query: a regra é só a
 * mescla chave a chave, e chave vazia ou faltando cai no genérico —
 * vocabulário pela metade deixaria a interface falando duas línguas
 * ("Alunos" no menu, "clientes" no corpo).
 */
export function resolveTerms(vocabulary?: Partial<PackVocabulary> | null): Terms {
  const resolved = {} as PackVocabulary;
  const title = {} as PackVocabulary;
  const gender = {} as Record<TermKey, TermGender>;

  for (const key of TERM_KEYS) {
    const value = vocabulary?.[key]?.trim();
    resolved[key] = value || GENERIC_TERMS[key];
    title[key] = capitalizeTerm(resolved[key]);
    gender[key] = guessGender(resolved[key]);
  }

  return {
    ...resolved,
    title,
    gender,
    agree: (masculine, key) => agree(masculine, gender[key]),
    hasPack: TERM_KEYS.some((key) => resolved[key] !== GENERIC_TERMS[key]),
  };
}

/**
 * Rotulagem, não i18n.
 *
 * O escopo é fechado em poucos substantivos: cliente/aluno/paciente/tutor,
 * serviço/aula/sessão, contrato/matrícula/pacote, agendamento/horário/sessão
 * e, para equipe, negócio/barbearia/clínica e profissional/barbeiro/psicóloga.
 * Frase inteira continua escrita em português no código — não crie chave de
 * tradução aqui.
 *
 * A fonte é o snapshot em `provider.vocabulary`, gravado pelo apply da
 * entrevista: editar o pack depois não muda o app de quem já aplicou.
 *
 * Em tela compartilhada com o cliente (a sidebar), passe `{ enabled: false }`
 * quando o usuário não for prestador: o hook continua lendo o cache e para de
 * pedir `/provider/me` para quem não tem perfil de prestador.
 */
export function useTerms(options?: { enabled?: boolean }): Terms {
  const { data: provider } = useProviderProfile(options);

  return useMemo(() => resolveTerms(provider?.vocabulary), [provider?.vocabulary]);
}
